/**
 * Password-change request shaping and error reading.
 *
 * The canonical auth-api endpoint is:
 *
 *     POST /api/auth/password/change   (@token_required)
 *     body: { "old_password": "...", "new_password": "..." }
 *
 * Two things about that contract are easy to get wrong, and both were wrong
 * before this module existed:
 *
 *   1. The path is /auth/password/change, not /auth/change-password. The
 *      latter has never been served by auth-api and returned a gunicorn HTML
 *      404, which no JSON error reader can make sense of.
 *   2. The fields are snake_case. auth-api reads data.get('old_password') and
 *      data.get('new_password'); camelCase keys parse as absent and come back
 *      as "Current password is required" even when the user filled the form in.
 *
 * No username is sent: auth-api resolves the account from the bearer token's
 * user_id, so a username in the body is ignored.
 */

/**
 * Build the request body auth-api actually expects.
 *
 * Accepts either `currentPassword` (what the Profile form binds) or
 * `oldPassword` (older callers), and normalises to the wire contract.
 *
 * @param {{currentPassword?: string, oldPassword?: string, newPassword?: string}} passwordData
 * @returns {{old_password: string, new_password: string}}
 */
export function buildChangePasswordRequest (passwordData = {}) {
  return {
    old_password: passwordData.currentPassword || passwordData.oldPassword || '',
    new_password: passwordData.newPassword || ''
  }
}

/**
 * Flatten one auth-api `details` entry into readable text.
 *
 * Password-strength failures arrive as an array of requirement strings
 * (`{ new_password: ["Must be at least 8 characters", ...] }`); simpler field
 * errors arrive as a single string.
 */
function flattenDetail (value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(' ')
  if (value && typeof value === 'object') return Object.values(value).map(flattenDetail).filter(Boolean).join(' ')
  return value ? String(value) : ''
}

/**
 * Turn a failed change-password response into the most specific message we can
 * honestly show the user.
 *
 * auth-api's error envelope is:
 *
 *     { success: false, message: "...", details: { field: "..." | [...] } }
 *
 * `details` is the useful half. On a validation failure the top-level message
 * is only ever "Password change validation failed", which tells the user
 * nothing; the per-field details say which field and why. So details win when
 * present, and we fall back through message -> Error.message -> a generic line.
 *
 * A non-JSON body (an HTML 404/502 from the edge or gunicorn) must never be
 * echoed into the UI, so anything that isn't a plain object is ignored.
 *
 * @param {any} error - an axios error, or a rejected response payload
 * @param {string} [fallback]
 * @returns {string}
 */
export function changePasswordErrorMessage (error, fallback = 'Failed to update password. Please try again.') {
  const body = error?.response?.data

  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const details = body.details
    if (details && typeof details === 'object') {
      const flattened = Object.keys(details)
        .map((key) => flattenDetail(details[key]))
        .filter(Boolean)
      if (flattened.length) return flattened.join(' ')
    }

    if (typeof body.message === 'string' && body.message.trim()) return body.message.trim()
  }

  if (typeof error?.message === 'string' && error.message.trim() && !/^Request failed with status code/i.test(error.message)) {
    return error.message.trim()
  }

  return fallback
}
